import { ReactNode } from 'react';
import { CoreBox, CoreTypography } from '@youscience/core';
import { ScholarshipDetail } from '@common-types';

import { Container } from '@components/Layout/components/Container';

import {
  DISABILITY_DATA,
  CLASSIFICATION_DATA,
  LEVEL_OF_STUDY_DATA,
  INSTITUTION_TYPE_DATA,
  ETHNICITY_LABELS,
  AWARD_INCLUSIONS_DATA,
  AWARD_CATEGORIES_DATA,
} from '@constants/scholarshipDetail';

import EthnicityIcon from '@images/ScholarshipsIcons/ethnicity.svg?react';
import MaleIcon from '@images/ScholarshipsIcons/male.svg?react';
import FemaleIcon from '@images/ScholarshipsIcons/female.svg?react';
import FamilyIcon from '@images/ScholarshipsIcons/family.svg?react';
import PersonIcon from '@images/ScholarshipsIcons/person.svg?react';
import AdultIcon from '@images/ScholarshipsIcons/self-improvement.svg?react';
import TimerIcon from '@images/ScholarshipsIcons/timer.svg?react';

import { sxStyles, StyledSectionInner, StyledCategoryItem } from './AdditionallySection.styles';

const renderCategoryItem = (icon: ReactNode, fieldName: string) => {
  return (
    <StyledCategoryItem key={fieldName}>
      <CoreBox>{icon}</CoreBox>

      <CoreTypography>{fieldName}</CoreTypography>
    </StyledCategoryItem>
  );
};

interface AdditionallySectionProps {
  scholarship: ScholarshipDetail;
}

export const AdditionallySection = (props: AdditionallySectionProps) => {
  const { scholarship } = props;
  const {
    gender,
    workStatus,
    maritalStatus,
    race,
    classification,
    disabilityType,
    levelOfStudy,
    institutionType,
    awardInclusions,
    programCategories,
  } = scholarship;

  const hasClassification = classification && Object.values(classification).some((classification) => classification);
  const hasInstitutionType = institutionType && Object.values(institutionType).some((type) => type);
  const hasLevelOfStudy = levelOfStudy && Object.values(levelOfStudy).some((level) => level);
  const hasOtherCategories =
    gender?.menOnly ||
    gender?.womenOnly ||
    maritalStatus?.single ||
    maritalStatus?.married ||
    programCategories?.adult ||
    workStatus?.fullOrPartTime ||
    workStatus?.partTimeOnly;
  const hasDisabilityType = disabilityType && Object.values(disabilityType).some((type) => type);
  const hasEthnicity = race && Object.values(race).some((race) => race);
  const hasAwardInclusions = race && Object.values(awardInclusions).some((award) => award);
  const hasAwardCategories = race && Object.values(programCategories).some((category) => category);

  if (
    !hasClassification &&
    !hasInstitutionType &&
    !hasLevelOfStudy &&
    !hasOtherCategories &&
    !hasDisabilityType &&
    !hasEthnicity &&
    !hasAwardInclusions &&
    !hasAwardCategories
  )
    return null;

  return (
    <Container>
      <StyledSectionInner>
        <CoreTypography sx={sxStyles.mainTitle} variant="h5" component="h4">
          Additionally
        </CoreTypography>

        <CoreBox sx={sxStyles.categoriesWrap}>
          {hasClassification && (
            <CoreBox>
              <CoreTypography sx={sxStyles.categoryTitle} variant="overline" component="h5">
                Award classification
              </CoreTypography>

              <CoreBox sx={sxStyles.categoryItemsWrap}>
                {Object.entries(classification).map(
                  ([key, value]) =>
                    value && renderCategoryItem(CLASSIFICATION_DATA[key].icon, CLASSIFICATION_DATA[key].label),
                )}
              </CoreBox>
            </CoreBox>
          )}

          {hasInstitutionType && (
            <CoreBox>
              <CoreTypography sx={sxStyles.categoryTitle} variant="overline" component="h5">
                Institution type
              </CoreTypography>

              <CoreBox sx={sxStyles.categoryItemsWrap}>
                {Object.entries(institutionType).map(
                  ([key, value]) =>
                    value && renderCategoryItem(INSTITUTION_TYPE_DATA[key].icon, INSTITUTION_TYPE_DATA[key].label),
                )}
              </CoreBox>
            </CoreBox>
          )}

          {hasLevelOfStudy && (
            <CoreBox>
              <CoreTypography sx={sxStyles.categoryTitle} variant="overline" component="h5">
                Level of study
              </CoreTypography>

              <CoreBox sx={sxStyles.categoryItemsWrap}>
                {Object.entries(levelOfStudy).map(
                  ([key, value]) =>
                    value && renderCategoryItem(LEVEL_OF_STUDY_DATA[key].icon, LEVEL_OF_STUDY_DATA[key].label),
                )}
              </CoreBox>
            </CoreBox>
          )}

          {hasOtherCategories && (
            <CoreBox>
              <CoreTypography sx={sxStyles.categoryTitle} variant="overline" component="h5">
                Other categories
              </CoreTypography>

              <CoreBox sx={sxStyles.categoryItemsWrap}>
                {gender?.menOnly && renderCategoryItem(<MaleIcon />, 'Male')}

                {gender?.womenOnly && renderCategoryItem(<FemaleIcon />, 'Female')}

                {maritalStatus?.married && renderCategoryItem(<FamilyIcon />, 'Married')}

                {maritalStatus?.single && renderCategoryItem(<PersonIcon />, 'Single')}

                {programCategories?.adult && renderCategoryItem(<AdultIcon />, 'Adult learning')}

                {(workStatus?.partTimeOnly || workStatus?.fullOrPartTime) &&
                  renderCategoryItem(<TimerIcon />, 'Part-time student')}
              </CoreBox>
            </CoreBox>
          )}

          {hasDisabilityType && (
            <CoreBox>
              <CoreTypography sx={sxStyles.categoryTitle} variant="overline" component="h5">
                Disability
              </CoreTypography>

              <CoreBox sx={sxStyles.categoryItemsWrap}>
                {Object.entries(disabilityType).map(
                  ([key, value]) => value && renderCategoryItem(DISABILITY_DATA[key].icon, DISABILITY_DATA[key].label),
                )}
              </CoreBox>
            </CoreBox>
          )}

          {hasEthnicity && (
            <CoreBox>
              <CoreTypography sx={sxStyles.categoryTitle} variant="overline" component="h5">
                Ethnicity
              </CoreTypography>

              <CoreBox sx={sxStyles.categoryItemsWrap}>
                {Object.entries(race).map(
                  ([key, value]) => value && renderCategoryItem(<EthnicityIcon />, ETHNICITY_LABELS[key]),
                )}
              </CoreBox>
            </CoreBox>
          )}

          {hasAwardInclusions && (
            <CoreBox>
              <CoreTypography sx={sxStyles.categoryTitle} variant="overline" component="h5">
                Award inclusions
              </CoreTypography>

              <CoreBox sx={sxStyles.categoryItemsWrap}>
                {Object.entries(awardInclusions).map(
                  ([key, value]) =>
                    value && renderCategoryItem(AWARD_INCLUSIONS_DATA[key].icon, AWARD_INCLUSIONS_DATA[key].label),
                )}
              </CoreBox>
            </CoreBox>
          )}

          {hasAwardCategories && (
            <CoreBox>
              <CoreTypography sx={sxStyles.categoryTitle} variant="overline" component="h5">
                Award Categories
              </CoreTypography>

              <CoreBox sx={sxStyles.categoryItemsWrap}>
                {Object.entries(programCategories).map(
                  ([key, value]) =>
                    value && renderCategoryItem(AWARD_CATEGORIES_DATA[key].icon, AWARD_CATEGORIES_DATA[key].label),
                )}
              </CoreBox>
            </CoreBox>
          )}
        </CoreBox>
      </StyledSectionInner>
    </Container>
  );
};
